import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import { UserRoundIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

export const STATUS_OPTIONS: ReadonlyArray<{
  readonly value: CmmdTaskStatus | "all";
  readonly label: string;
}> = [
  { value: "all", label: "All statuses" },
  { value: "backlog", label: "Backlog" },
  { value: "todo", label: "Todo" },
  { value: "in_progress", label: "In Progress" },
  { value: "in_review", label: "In Review" },
  { value: "blocked", label: "Blocked" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];

interface FilterSelectProps {
  readonly label: string;
  readonly ariaLabel: string;
  readonly value: string;
  readonly options: ReadonlyArray<FilterOption>;
  readonly onChange: (value: string) => void;
  readonly widthClassName: string;
  readonly controlClassName: string;
}

export function FilterSelect({
  label,
  ariaLabel,
  value,
  options,
  onChange,
  widthClassName,
  controlClassName,
}: FilterSelectProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label ?? label;
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (next !== null) onChange(next);
      }}
      items={options.map((option) => ({ value: option.value, label: option.label }))}
    >
      <SelectTrigger
        size="sm"
        className={cn(
          controlClassName,
          "inline-flex w-full min-w-0 whitespace-nowrap px-2",
          widthClassName,
        )}
        aria-label={ariaLabel}
        title={selectedLabel}
      >
        <SelectValue className="truncate text-xs font-medium text-foreground/95">
          {selectedLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectPopup align="start" alignItemWithTrigger={false}>
        <SelectGroup>
          <SelectGroupLabel>{label}</SelectGroupLabel>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} hideIndicator>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectPopup>
    </Select>
  );
}

interface AssigneeSelectProps {
  readonly value: string;
  readonly options: ReadonlyArray<FilterOption>;
  readonly onChange: (value: string) => void;
  readonly widthClassName: string;
  readonly controlClassName: string;
}

export function AssigneeSelect({
  value,
  options,
  onChange,
  widthClassName,
  controlClassName,
}: AssigneeSelectProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label ?? "All assignees";
  return (
    <div className={cn("min-w-0 w-full", widthClassName)}>
      <Select
        value={value}
        onValueChange={(next) => {
          if (next !== null) onChange(next);
        }}
        items={options.map((option) => ({ value: option.value, label: option.label }))}
      >
        <SelectTrigger
          size="sm"
          className={cn(controlClassName, "inline-flex w-full min-w-0 whitespace-nowrap px-2")}
          aria-label="Assignee filter"
          title={selectedLabel}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <UserRoundIcon className="size-3 shrink-0 text-muted-foreground/70" />
            <SelectValue className="truncate text-xs font-medium text-foreground/95">
              {selectedLabel}
            </SelectValue>
          </span>
        </SelectTrigger>
        <SelectPopup align="start" alignItemWithTrigger={false}>
          <SelectGroup>
            <SelectGroupLabel>Assignee</SelectGroupLabel>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} hideIndicator>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectPopup>
      </Select>
    </div>
  );
}
